import { MihatoMark } from "@/common/components/MihatoMark";
import { gsap } from "gsap";
import { useEffect, useMemo, useRef } from "react";
import { LandingIcon } from "./LandingIcon";
import { CornerBracket } from "./LandingMark";

const GRID_COLS = 12;
const GRID_ROWS = 10;

// Parcelas de una finca vista aérea. [col, row, colSpan, rowSpan, variant].
// "active" = potrero con ganado (pulsa verde); "pasture" = pasto (tinte verde sutil);
// "field" = lote neutro; "divider" = franja angosta entre lotes (tenue, no negra).
type ParcelVariant = "active" | "pasture" | "field" | "divider";
const PARCELS: ReadonlyArray<readonly [number, number, number, number, ParcelVariant]> = [
  // Franja superior (rows 1-3)
  [1, 1, 4, 3, "pasture"],
  [5, 1, 3, 2, "field"],
  [8, 1, 2, 2, "active"],
  [10, 1, 3, 3, "pasture"],
  [5, 3, 3, 1, "divider"],
  [8, 3, 2, 1, "field"],
  // Franja media (rows 4-6)
  [1, 4, 2, 2, "field"],
  [1, 6, 2, 1, "divider"],
  [3, 4, 3, 3, "pasture"],
  [6, 4, 3, 1, "divider"],
  [6, 5, 3, 2, "active"],
  [9, 4, 4, 2, "pasture"],
  [9, 6, 4, 1, "divider"],
  // Franja inferior (rows 7-10)
  [1, 7, 3, 2, "field"],
  [1, 9, 3, 2, "pasture"],
  [4, 7, 3, 4, "pasture"],
  [7, 7, 3, 2, "field"],
  [7, 9, 3, 2, "active"],
  [10, 7, 3, 4, "pasture"],
];

// Pseudo-random determinista por índice para que SSR/HMR no salten posiciones.
function seededRandom(seed: number) {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

type Dot = { top: string; left: string };

// Distribución no uniforme: la mayoría de parcelas queda como antes (o vacía),
// pero algunas específicas reciben un boost para simular concentraciones de
// ganado reales (un potrero más ocupado, otros casi vacíos).
const DENSE_PARCELS: ReadonlySet<number> = new Set([0, 10, 18]); // pasture grande, active centro, pasture esquina

function dotCount(variant: ParcelVariant, parcelIdx: number): number {
  if (variant === "active") {
    // active: base 3, una de las activas (10) ya está en DENSE → 6
    return DENSE_PARCELS.has(parcelIdx) ? 6 : 3;
  }
  if (variant === "pasture") {
    if (DENSE_PARCELS.has(parcelIdx)) return 5;
    // pastures normales: la mitad con 2 puntos, la otra mitad vacías (como antes)
    return parcelIdx % 2 === 0 ? 2 : 0;
  }
  return 0;
}

function buildDots(variant: ParcelVariant, parcelIdx: number): Dot[] {
  const count = dotCount(variant, parcelIdx);
  return Array.from({ length: count }, (_, i) => {
    const seed = parcelIdx * 17 + i * 31;
    return {
      top: `${12 + seededRandom(seed) * 76}%`,
      left: `${12 + seededRandom(seed + 7) * 76}%`,
    };
  });
}

export function LandingHero() {
  const sectionRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const wordsRef = useRef<HTMLHeadingElement>(null);
  const fadeRef = useRef<HTMLDivElement>(null);

  const dotsByParcel = useMemo(
    () => PARCELS.map(([, , , , variant], idx) => buildDots(variant, idx)),
    [],
  );

  useEffect(() => {
    const gridEl = gridRef.current;
    const wordsEl = wordsRef.current;
    const fadeEl = fadeRef.current;
    const sectionEl = sectionRef.current;
    if (!gridEl || !wordsEl || !fadeEl || !sectionEl) return;

    let sweep: ReturnType<typeof setInterval> | undefined;
    let flicker: ReturnType<typeof setInterval> | undefined;

    const ctx = gsap.context(() => {
      const parcels = gridEl.querySelectorAll<HTMLDivElement>(".parcel");

      for (const parcel of parcels) {
        const variant = parcel.dataset.variant as ParcelVariant;

        if (variant === "active") {
          gsap.to(parcel, {
            backgroundColor: "rgba(0, 229, 153, random(0.15, 0.35))",
            borderColor: "rgba(0, 229, 153, random(0.3, 0.6))",
            boxShadow: "0 0 25px rgba(0, 229, 153, 0.25)",
            duration: "random(2, 3.5)",
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
            delay: "random(0, 1.5)",
          });
        } else if (variant === "pasture") {
          gsap.to(parcel, {
            backgroundColor: "rgba(0, 229, 153, random(0.02, 0.09))",
            borderColor: "rgba(0, 229, 153, random(0.06, 0.18))",
            duration: "random(3, 6)",
            repeat: -1,
            yoyo: true,
            ease: "power1.inOut",
            delay: "random(0, 3)",
          });
        } else if (variant === "divider") {
          gsap.to(parcel, {
            backgroundColor: "rgba(255, 255, 255, random(0.005, 0.02))",
            borderColor: "rgba(255, 255, 255, random(0.02, 0.05))",
            duration: "random(5, 9)",
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut",
            delay: "random(0, 3)",
          });
        } else {
          gsap.to(parcel, {
            backgroundColor: "rgba(255, 255, 255, random(0.01, 0.05))",
            borderColor: "rgba(255, 255, 255, random(0.03, 0.1))",
            duration: "random(2.5, 5)",
            repeat: -1,
            yoyo: true,
            ease: "power1.inOut",
            delay: "random(0, 3)",
          });
        }
      }

      // Flicker aleatorio — una parcela random parpadea cada ~2.5s, imita el
      // "aparece/desaparece" de la versión original con 120 cuadros.
      // Cattle dots — puntos blancos tenues que respiran (in/out) dentro de
      // potreros activos y algunos potreros con pasto. Simulan ganado visto
      // desde el aire.
      const dots = gridEl.querySelectorAll<HTMLSpanElement>(".cattle-dot");
      for (const dot of dots) {
        gsap.set(dot, { opacity: 0 });
        gsap.to(dot, {
          opacity: "random(0.3, 0.55)",
          duration: "random(1.8, 3.2)",
          delay: "random(0, 4)",
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
        gsap.to(dot, {
          x: "random(-2, 2)",
          y: "random(-2, 2)",
          duration: "random(4, 7)",
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: "random(0, 2)",
        });
      }

      flicker = setInterval(() => {
        const pick = parcels[Math.floor(Math.random() * parcels.length)];
        if (!pick || pick.dataset.variant === "active") return;
        gsap.to(pick, {
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          borderColor: "rgba(255, 255, 255, 0.35)",
          duration: 0.35,
          yoyo: true,
          repeat: 1,
          ease: "power2.out",
        });
      }, 2500);

      sweep = setInterval(() => {
        for (const parcel of parcels) {
          const col = Number(parcel.dataset.col ?? 1);
          gsap.to(parcel, {
            backgroundColor: "rgba(255, 255, 255, 0.28)",
            borderColor: "rgba(255, 255, 255, 0.55)",
            boxShadow: "0 0 25px rgba(255, 255, 255, 0.18)",
            duration: 0.5,
            yoyo: true,
            repeat: 1,
            delay: (col - 1) * 0.06,
            ease: "power2.out",
          });
        }
      }, 9000);

      const tl = gsap.timeline();
      tl.to(wordsEl.querySelectorAll(".hero-word"), {
        y: "0%",
        duration: 1,
        stagger: 0.08,
        ease: "power4.out",
        delay: 0.2,
      }).to(
        sectionEl.querySelectorAll(".hero-fade"),
        {
          opacity: 1,
          y: 0,
          duration: 1,
          stagger: 0.15,
          ease: "power2.out",
        },
        "-=0.6",
      );
    }, sectionEl);

    return () => {
      if (sweep) clearInterval(sweep);
      if (flicker) clearInterval(flicker);
      ctx.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative z-10 flex flex-col items-center overflow-hidden border-white/[0.06] border-b pt-24 pb-0"
    >
      <CornerBracket position="tl" />
      <CornerBracket position="tr" />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center opacity-40"
        style={{ perspective: "1000px" }}
      >
        <div
          ref={gridRef}
          className="grid h-[600px] w-[800px] max-w-full gap-1"
          style={{
            gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${GRID_ROWS}, minmax(0, 1fr))`,
            transform: "rotateX(20deg)",
          }}
        >
          {PARCELS.map(([col, row, colSpan, rowSpan, variant], idx) => {
            const baseClass =
              variant === "divider"
                ? "border-white/[0.02] bg-white/[0.008]"
                : variant === "pasture"
                  ? "border-primary-500/10 bg-primary-500/[0.03]"
                  : variant === "active"
                    ? "border-primary-500/30 bg-primary-500/[0.08]"
                    : "border-white/[0.04] bg-white/[0.015]";
            const dots = dotsByParcel[idx] ?? [];
            return (
              <div
                key={`${col}-${row}`}
                data-col={col}
                data-variant={variant}
                className={`parcel relative rounded-[3px] border ${baseClass}`}
                style={{
                  gridColumn: `${col} / span ${colSpan}`,
                  gridRow: `${row} / span ${rowSpan}`,
                }}
              >
                {dots.map((dot, dotIdx) => (
                  <span
                    // biome-ignore lint/suspicious/noArrayIndexKey: posiciones estables, índice basta
                    key={dotIdx}
                    className="cattle-dot pointer-events-none absolute h-[3px] w-[3px] rounded-full bg-white"
                    style={{
                      top: dot.top,
                      left: dot.left,
                      boxShadow: "0 0 4px rgba(255, 255, 255, 0.4)",
                    }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className="relative z-10 mt-10 flex w-full max-w-4xl flex-col items-center px-6 text-center">
        <h1
          ref={wordsRef}
          className="relative mb-8 font-medium text-5xl leading-[1.05] tracking-tight md:text-7xl lg:text-[5rem]"
        >
          <span className="mask-wrapper">
            <span className="hero-word mask-inner text-neutral-500">Tu</span>
          </span>{" "}
          <span className="mask-wrapper">
            <span className="hero-word mask-inner">hato</span>
          </span>{" "}
          <span className="mask-wrapper">
            <span className="hero-word mask-inner text-neutral-500">como</span>
          </span>{" "}
          <span className="mask-wrapper">
            <span className="hero-word mask-inner">activo,</span>
          </span>
          <br />
          <span className="mask-wrapper">
            <span className="hero-word mask-inner text-neutral-500">no</span>
          </span>{" "}
          <span className="mask-wrapper">
            <span className="hero-word mask-inner text-neutral-500">como</span>
          </span>{" "}
          <span className="mask-wrapper">
            <span className="hero-word mask-inner">gasto.</span>
          </span>
        </h1>

        <div ref={fadeRef} className="flex flex-col items-center">
          <p className="hero-fade mb-10 max-w-2xl translate-y-4 text-lg text-neutral-400 leading-relaxed opacity-0 md:text-xl">
            Costeo del hato bajo NIIF PYMES Sección 34. Capitalizá la crianza, deprecíalá
            correctamente, y exportá los asientos contables listos para tu contador.
          </p>

          <a
            href="/access-request"
            className="hero-fade group relative flex translate-y-4 items-center gap-3 rounded-full border border-white/[0.06] bg-brand-panel p-1 pr-6 opacity-0 transition-all duration-300 hover:border-primary-500/50"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-500/30 bg-primary-500/10 text-primary-500 transition-colors group-hover:bg-primary-500 group-hover:text-brand-dark">
              <LandingIcon name="arrow-right" size={18} />
            </span>
            <span className="font-medium text-sm text-white">Solicitar acceso</span>
          </a>
        </div>
      </div>

      <HeroMockup />
    </section>
  );
}

const MOCKUP_ROWS = [
  { id: "CR-104", name: "Estrella", breed: "Holstein · 4a", weight: "542", status: "Producción" },
  { id: "CR-211", name: "Luna", breed: "Hol-Jer · 2a", weight: "438", status: "Lactando" },
  { id: "CR-103", name: "Reina", breed: "Jersey · 3a", weight: "402", status: "Pronta" },
  { id: "CR-088", name: "Linda", breed: "Holstein · 5a", weight: "568", status: "Producción" },
] as const;

function HeroMockup() {
  return (
    <div className="hero-fade relative mt-64 w-[95%] max-w-6xl translate-y-10 opacity-0 md:mt-80">
      {/* glow behind the mockup */}
      <div
        aria-hidden
        className="-inset-x-20 -top-10 pointer-events-none absolute inset-y-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0, 229, 153, 0.18) 0%, transparent 60%)",
          filter: "blur(60px)",
        }}
      />

      {/* top accent line */}
      <div
        className="relative top-[1px] z-20 mx-auto h-px w-3/4 bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-60"
        style={{ boxShadow: "0 -6px 30px rgba(0, 229, 153, 0.45)" }}
      />

      {/* browser frame */}
      <div className="relative overflow-hidden rounded-t-2xl border border-white/[0.08] bg-brand-elevated shadow-[0_30px_120px_-20px_rgba(0,229,153,0.25)]">
        {/* title bar */}
        <div className="flex items-center gap-2 border-white/[0.06] border-b px-4 py-3">
          <div className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <div className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <div className="h-3 w-3 rounded-full bg-[#28c840]" />
          <div className="mx-auto flex items-center gap-1.5 rounded-md bg-white/[0.04] px-3 py-0.5 font-mono text-[11px] text-neutral-400">
            <LandingIcon name="lock-password" size={11} />
            hato.app/dashboard
          </div>
        </div>

        {/* app body — LIGHT theme, mirrors /dashboard */}
        <div className="flex min-h-[460px] bg-app-bg text-app-ink-1">
          {/* sidebar */}
          <aside className="hidden w-[190px] shrink-0 flex-col border-app-border border-r bg-app-surface md:flex">
            <div className="flex items-center gap-2 border-app-border border-b px-4 py-3.5">
              <MihatoMark className="h-5 w-5 text-app-ink-1" accent />
              <span className="font-semibold text-[13px] text-app-ink-1 tracking-tight">HATO</span>
            </div>
            <div className="mx-2.5 mt-2.5 flex items-center gap-2 rounded-md border border-app-border bg-app-surface-2 px-2.5 py-2">
              <div className="grid h-6 w-6 place-items-center rounded bg-app-ink-1 font-semibold text-[10px] text-white">
                SR
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium text-[11px] text-app-ink-1">
                  Hacienda Santa Rosa
                </div>
                <div className="text-[9px] text-app-ink-3">248 cabezas</div>
              </div>
            </div>
            <nav className="mt-3 flex flex-col px-2 text-[12px]">
              <div className="px-2 pb-1 font-medium text-[9px] text-app-ink-3 uppercase tracking-[0.08em]">
                Operaciones
              </div>
              <SidebarItem label="Hato" count="248" active />
              <SidebarItem label="Lotes" count="6" />
              <SidebarItem label="Costos" />
              <SidebarItem label="Depreciación" />
              <SidebarItem label="Reportes" />
            </nav>
          </aside>

          {/* main */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* topbar */}
            <div className="hidden items-center border-app-border border-b px-5 py-2.5 font-medium text-[11px] text-app-ink-2 md:flex">
              <span>Hacienda Santa Rosa</span>
              <span className="mx-1.5 text-app-ink-3">/</span>
              <span className="text-app-ink-1">Hato</span>
            </div>

            <div className="flex flex-1 gap-4 p-4 md:p-5">
              {/* content */}
              <div className="flex min-w-0 flex-1 flex-col gap-3.5">
                <div>
                  <h3 className="font-semibold text-[18px] text-app-ink-1 tracking-tight md:text-[22px]">
                    Hato productivo
                  </h3>
                  <p className="text-[10px] text-app-ink-3 md:text-[11px]">
                    Período abr 2026 · sync 14:42
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-2.5">
                  <MockupKpi label="Cabezas" value="248" delta="+2" />
                  <MockupKpi label="Productivas" value="152" delta="61%" positive />
                  <MockupKpi label="En crianza" value="93" delta="37%" />
                </div>

                <div className="overflow-hidden rounded-lg border border-app-border bg-app-surface">
                  <div className="hidden grid-cols-[90px_1fr_1fr_60px_90px] items-center gap-3 border-app-border border-b px-3 py-2 font-medium text-[9px] text-app-ink-3 uppercase tracking-wider md:grid">
                    <div>ID</div>
                    <div>Nombre</div>
                    <div>Raza</div>
                    <div>Peso</div>
                    <div>Estado</div>
                  </div>
                  {MOCKUP_ROWS.map((r, i) => (
                    <div
                      key={r.id}
                      className={`grid grid-cols-[90px_1fr_60px_90px] items-center gap-3 px-3 py-2 text-[11px] md:grid-cols-[90px_1fr_1fr_60px_90px] ${
                        i < MOCKUP_ROWS.length - 1 ? "border-app-border/60 border-b" : ""
                      }`}
                    >
                      <div className="rounded border border-app-border bg-app-surface-2 px-1.5 py-0.5 text-center font-mono text-[10px] text-app-ink-1">
                        {r.id}
                      </div>
                      <div className="truncate font-medium text-app-ink-1">{r.name}</div>
                      <div className="hidden truncate text-app-ink-3 md:block">{r.breed}</div>
                      <div className="font-mono text-app-ink-2">
                        {r.weight}
                        <span className="ml-0.5 text-app-ink-3">kg</span>
                      </div>
                      <StatusPill status={r.status} />
                    </div>
                  ))}
                </div>
              </div>

              {/* timeline card */}
              <aside className="hidden w-[220px] shrink-0 flex-col gap-2 rounded-lg border border-app-border bg-app-surface p-3 lg:flex">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[12px] text-app-ink-1">Evento · Reina</div>
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 font-medium text-[9px] text-emerald-700">
                    Capitalización
                  </span>
                </div>
                <ChatBubble who="HATO" time="14:08" system>
                  Primer parto · Reina (CR-103). Crianza capitalizada: ₡1.245.000.
                </ChatBubble>
                <ChatBubble who="Capataz" time="14:12">
                  Pasé al lote de producción.
                </ChatBubble>
                <ChatBubble who="Contador" time="14:30">
                  Asiento listo para el cierre del mes.
                </ChatBubble>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SidebarItem({
  label,
  active,
  count,
}: {
  label: string;
  active?: boolean;
  count?: string;
}) {
  return (
    <div
      className={`flex items-center gap-2 rounded px-2 py-1.5 font-medium text-[11px] ${
        active ? "bg-app-ink-1 text-white" : "text-app-ink-2"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
      <span className="flex-1">{label}</span>
      {count && (
        <span className={`font-mono text-[10px] ${active ? "text-neutral-400" : "text-app-ink-3"}`}>
          {count}
        </span>
      )}
    </div>
  );
}

function MockupKpi({
  label,
  value,
  delta,
  positive,
  danger,
}: {
  label: string;
  value: string;
  delta: string;
  positive?: boolean;
  danger?: boolean;
}) {
  const deltaColor = danger ? "text-rose-600" : positive ? "text-emerald-600" : "text-app-ink-3";
  return (
    <div className="rounded-lg border border-app-border bg-app-surface px-2.5 py-2">
      <div className="font-medium text-[8px] text-app-ink-3 uppercase tracking-wider md:text-[9px]">
        {label}
      </div>
      <div className="mt-1 font-semibold text-[18px] text-app-ink-1 leading-none md:text-[22px]">
        {value}
      </div>
      <div className={`mt-1 text-[9px] md:text-[10px] ${deltaColor}`}>{delta}</div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const style =
    status === "Producción" || status === "Lactando"
      ? "bg-emerald-100 text-emerald-700"
      : status === "Pronta" || status === "Seca"
        ? "bg-amber-100 text-amber-700"
        : "bg-app-surface-2 text-app-ink-2";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-medium text-[9px] ${style}`}
    >
      <span className="h-1 w-1 rounded-full bg-current" />
      <span className="truncate">{status}</span>
    </span>
  );
}

function ChatBubble({
  who,
  time,
  system,
  children,
}: {
  who: string;
  time: string;
  system?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5 text-[9px] text-app-ink-3">
        <span className="font-semibold text-app-ink-2">{who}</span>
        <span>·</span>
        <span className="font-mono">{time}</span>
      </div>
      <div
        className={`rounded-md px-2 py-1.5 text-[10px] leading-snug ${
          system ? "bg-app-ink-1 text-white" : "bg-app-surface-2 text-app-ink-1"
        }`}
      >
        {children}
      </div>
    </div>
  );
}
